import { SchemaType } from '../constants/schema.js'
import { DomainModelRole } from '../constants/cardTypes.js'
import { removeHtmlTags } from './validate.js'
import { isSingularPluralMatch, fieldNameIsEntityIdReference } from './fieldNameMatch.js'
import { fieldHasNoMatchingEntityField, walkCardFields } from './fieldEntityMatch.js'

export const Severity = Object.freeze({
  MAJOR: 'major',
  MINOR: 'minor',
  NOTICE: 'notice'
})

export const validateDomainModel = (workflow, boundedContext) => {
  const issues = []

  if (!workflow || !boundedContext) {
    return { issues }
  }

  const schemas = workflow.schemas || []
  const domainEvents = workflow.domainEvents || []
  const isGlobalContext = boundedContext.id === null || typeof boundedContext.id === 'undefined'

  const entitiesMap = new Map()
  const contextEntityIds = new Set()

  for (const schema of schemas) {
    if (schema.type === SchemaType.ENTITY) {
      entitiesMap.set(schema.id, schema)
      if (isGlobalContext || schema.boundedContext === boundedContext.id) {
        contextEntityIds.add(schema.id)
      }
    }
  }

  const contextEvents = isGlobalContext
    ? domainEvents
    : domainEvents.filter((event) => {
      const entityCard = event.cards?.find((card) =>
        card.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT
      )

      return entityCard && contextEntityIds.has(entityCard.schemaId)
    })

  for (const entityId of contextEntityIds) {
    const entity = entitiesMap.get(entityId)
    if (!entity) {
      continue
    }

    for (const field of entity.fields || []) {
      if (field.relatedEntityId) {
        const relatedEntity = entitiesMap.get(field.relatedEntityId)
        if (!relatedEntity) {
          issues.push({
            severity: Severity.MINOR,
            type: 'entity',
            id: entity.id,
            name: entity.name,
            field: field.name,
            code: 'INVALID_ENTITY_REFERENCE',
            message: `Field "${field.name}" on entity "${entity.name}" references a non-existent entity`
          })
        }
      }
    }
  }

  const entityNameMap = new Map()
  for (const entityId of contextEntityIds) {
    const entity = entitiesMap.get(entityId)
    if (!entity?.name) continue

    const lowerName = entity.name.toLowerCase()
    if (!entityNameMap.has(lowerName)) {
      entityNameMap.set(lowerName, [entity])
    } else {
      entityNameMap.get(lowerName).push(entity)
    }
  }

  for (const [, duplicateEntities] of entityNameMap) {
    if (duplicateEntities.length > 1) {
      for (const entity of duplicateEntities.slice(1)) {
        issues.push({
          severity: Severity.MINOR,
          type: 'entity',
          id: entity.id,
          name: entity.name,
          code: 'DUPLICATE_ENTITY_NAME',
          message: `Multiple entities with the name "${duplicateEntities[0].name}" in the same bounded context`
        })
      }
    }
  }

  const lanes = workflow.lanes || []
  const checkedLaneIds = new Set()
  for (const event of contextEvents) {
    if (!event.laneId || checkedLaneIds.has(event.laneId)) continue
    checkedLaneIds.add(event.laneId)

    const lane = lanes.find((l) => l.id === event.laneId)
    if (!lane?.name?.trim()) {
      issues.push({
        severity: Severity.MAJOR,
        type: 'role',
        id: event.laneId,
        name: 'Unnamed role',
        code: 'MISSING_NAME',
        message: 'A role has no name'
      })
    }
  }

  const eventNameMap = new Map()
  for (const event of contextEvents) {
    const eventName = removeHtmlTags(event.description || '').trim()
    if (!eventName) continue

    if (!eventNameMap.has(eventName)) {
      eventNameMap.set(eventName, [event])
    } else {
      eventNameMap.get(eventName).push(event)
    }
  }

  for (const [eventName, events] of eventNameMap) {
    if (events.length > 1) {
      issues.push({
        severity: Severity.MAJOR,
        type: 'event',
        id: events[1].id,
        name: eventName,
        code: 'DUPLICATE_EVENT',
        message: `Multiple domain events with the name "${eventName}"`
      })
    }
  }

  for (const event of contextEvents) {
    const entityCard = event.cards?.find((card) =>
      card.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT
    )
    const associatedEntity = entityCard?.schemaId ? entitiesMap.get(entityCard.schemaId) : null

    for (const card of event.cards || []) {
      const domainRole = card.cardType?.domainModelRole
      const issuesBefore = issues.length

      if (domainRole === DomainModelRole.COMMAND) {
        validateCommand(card, associatedEntity, schemas, entitiesMap, issues)
      } else if (domainRole === DomainModelRole.READ_MODEL) {
        validateReadModel(card, associatedEntity, schemas, entitiesMap, issues)
      }

      for (let i = issuesBefore; i < issues.length; i++) {
        issues[i].schemaId = card.schemaId
      }
    }
  }

  return { issues: deduplicateIssues(issues) }
}

const deduplicateIssues = (issues) => {
  const seen = new Set()
  const result = []
  for (const issue of issues) {
    const key = [
      issue?.type,
      issue?.code,
      issue?.schemaId ?? issue?.id,
      issue?.field,
      issue?.message
    ].filter(Boolean).join('|')
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    result.push(issue)
  }
  return result
}

const validateCommand = (card, associatedEntity, schemas, entitiesMap, issues) => {
  const commandSchema = card.schemaId
    ? schemas.find((s) => s.id === card.schemaId)
    : null

  const commandName = removeHtmlTags(card.description || '').trim()
  const fields = commandSchema?.fields || []

  if (!commandName) {
    issues.push({
      severity: Severity.MAJOR,
      type: 'command',
      id: card.id,
      name: 'Unnamed command',
      code: 'MISSING_NAME',
      message: 'A command has no name'
    })

    return
  }

  if (fields.length === 0) {
    issues.push({
      severity: Severity.MAJOR,
      type: 'command',
      id: card.id,
      name: commandName,
      code: 'NO_PARAMETERS',
      message: `Command "${commandName}" has no parameters`
    })

    return
  }

  if (!associatedEntity) {
    return
  }

  const entityFields = associatedEntity.fields || []
  const relatedEntities = entityFields
    .filter((f) => f.relatedEntityId)
    .map((f) => entitiesMap.get(f.relatedEntityId))
    .filter(Boolean)
  const targetedOneToManyEntities = new Map()

  for (const field of fields) {
    const matchingEntityField = entityFields.find((f) => f.name === field.name) ||
      entityFields.find((f) => isSingularPluralMatch(f.name, field.name))
    if (!matchingEntityField) {
      if (!field.relatedEntityId) {
        for (const relatedEntity of relatedEntities) {
          const matchesRelated = (relatedEntity.fields || []).some((f) => {
            if (fieldNameIsEntityIdReference(f.name, associatedEntity.name)) {
              return false
            }
            if (f.relatedEntityId === associatedEntity.id) {
              return false
            }

            return f.name === field.name || isSingularPluralMatch(f.name, field.name)
          })
          if (matchesRelated) {
            const isOneToMany = entityFields.some(
              (f) => f.relatedEntityId === relatedEntity.id && f.cardinality === 'one-to-many'
            )
            if (isOneToMany && !targetedOneToManyEntities.has(relatedEntity.id)) {
              targetedOneToManyEntities.set(relatedEntity.id, relatedEntity)
            }
          }
        }
      }

      if (fieldHasNoMatchingEntityField(associatedEntity.id, field, schemas)) {
        issues.push({
          severity: Severity.MINOR,
          type: 'command',
          id: card.id,
          name: commandName,
          field: field.name,
          code: 'FIELD_NOT_IN_ENTITY',
          message: `Field "${field.name}" on command "${commandName}" has no matching field on entity "${associatedEntity.name}"`
        })
      }
      continue
    }

    if (field.relatedEntityId && !entitiesMap.has(field.relatedEntityId)) {
      issues.push({
        severity: Severity.MINOR,
        type: 'command',
        id: card.id,
        name: commandName,
        field: field.name,
        code: 'INVALID_ENTITY_REFERENCE',
        message: `Field "${field.name}" on command "${commandName}" references a non-existent entity`
      })
      continue
    }

    const entityField = matchingEntityField
    if (entityField?.relatedEntityId && field.relatedEntityId !== entityField.relatedEntityId) {
      const expectedEntity = entitiesMap.get(entityField.relatedEntityId)
      const actualEntity = field.relatedEntityId ? entitiesMap.get(field.relatedEntityId) : null

      if (expectedEntity) {
        issues.push({
          severity: Severity.MAJOR,
          type: 'command',
          id: card.id,
          name: commandName,
          field: field.name,
          code: 'ENTITY_REFERENCE_MISMATCH',
          message: actualEntity
            ? `Field "${field.name}" on command "${commandName}" references "${actualEntity.name}" but entity field references "${expectedEntity.name}"`
            : `Field "${field.name}" on command "${commandName}" should reference "${expectedEntity.name}" but no entity is selected`
        })
      }
    } else if (!entityField?.relatedEntityId && field.relatedEntityId) {
      const actualEntity = entitiesMap.get(field.relatedEntityId)
      if (actualEntity) {
        issues.push({
          severity: Severity.MAJOR,
          type: 'command',
          id: card.id,
          name: commandName,
          field: field.name,
          code: 'ENTITY_REFERENCE_MISMATCH',
          message: `Field "${field.name}" on command "${commandName}" references "${actualEntity.name}" but the entity field has no reference`
        })
      }
    }

    if (field.relatedEntityId && Array.isArray(field.fields) && field.fields.length > 0) {
      walkCardFields(field.relatedEntityId, field.fields, schemas, ({ field: subField, parentEntityId, path }) => {
        if (!fieldHasNoMatchingEntityField(parentEntityId, subField, schemas)) {
          return
        }
        const referencedEntity = entitiesMap.get(parentEntityId)
        const fullPath = [field.name, ...path].join('.')
        const parentPath = path.length === 1
          ? field.name
          : [field.name, ...path.slice(0, -1)].join('.')
        issues.push({
          severity: Severity.MINOR,
          type: 'command',
          id: card.id,
          name: commandName,
          field: fullPath,
          code: 'SUB_FIELD_NOT_IN_ENTITY',
          message: `Sub-field "${subField.name}" on command "${commandName}" (under "${parentPath}") has no matching field on entity "${referencedEntity?.name || 'unknown'}"`
        })
      })
    }
  }

  const normalizedCommandName = commandName.replace(/\s+/g, '').toLowerCase()
  const normalizedAggregateName = associatedEntity.name.replace(/\s+/g, '').toLowerCase()
  const competingNames = [
    normalizedAggregateName,
    ...relatedEntities.map((e) => e.name.replace(/\s+/g, '').toLowerCase())
  ]
  for (const relatedEntity of relatedEntities) {
    if (targetedOneToManyEntities.has(relatedEntity.id)) continue
    const isOneToMany = entityFields.some(
      (f) => f.relatedEntityId === relatedEntity.id && f.cardinality === 'one-to-many'
    )
    if (!isOneToMany) continue
    const normalizedEntityName = relatedEntity.name.replace(/\s+/g, '').toLowerCase()
    if (!normalizedCommandName.includes(normalizedEntityName)) continue

    const absorbedByLongerName = competingNames.some((longer) =>
      longer.length > normalizedEntityName.length &&
      longer.includes(normalizedEntityName) &&
      normalizedCommandName.includes(longer)
    )
    if (absorbedByLongerName) continue

    targetedOneToManyEntities.set(relatedEntity.id, relatedEntity)
  }

  for (const [relatedEntityId, relatedEntity] of targetedOneToManyEntities) {
    const hasIdentifier = fields.some((f) => {
      if (fieldNameIsEntityIdReference(f.name, relatedEntity.name)) {
        return true
      }

      return f.relatedEntityId === relatedEntityId
    })

    if (!hasIdentifier) {
      const isValueObject = !(relatedEntity.fields || []).some((f) => f.name === 'id')
      issues.push({
        severity: Severity.MINOR,
        type: 'command',
        id: card.id,
        name: commandName,
        code: 'MISSING_COLLECTION_IDENTIFIER',
        message: isValueObject
          ? `Command "${commandName}" targets "${relatedEntity.name}" (value objects) — needs at least 2 fields`
          : `Command "${commandName}" modifies "${relatedEntity.name}" (collection) but has no field to identify which item`
      })
    }
  }
}

const validateReadModel = (card, associatedEntity, schemas, entitiesMap, issues) => {
  const querySchema = card.schemaId
    ? schemas.find((s) => s.id === card.schemaId)
    : null

  const readModelName = removeHtmlTags(card.description || '').trim()

  if (!readModelName) {
    issues.push({
      severity: Severity.MAJOR,
      type: 'readmodel',
      id: card.id,
      name: 'Unnamed read model',
      code: 'MISSING_NAME',
      message: 'A read model has no name'
    })

    return
  }

  if (!querySchema?.entityId) {
    issues.push({
      severity: Severity.NOTICE,
      type: 'readmodel',
      id: card.id,
      name: readModelName,
      code: 'MISSING_ENTITY',
      message: `Read model "${readModelName}" has no entity reference`
    })

    return
  }

  const linkedEntity = entitiesMap.get(querySchema.entityId)
  if (!linkedEntity) {
    issues.push({
      severity: Severity.MAJOR,
      type: 'readmodel',
      id: card.id,
      name: readModelName,
      code: 'INVALID_ENTITY',
      message: `Read model "${readModelName}" references a non-existent entity`
    })

    return
  }

  const entityFields = linkedEntity.fields || []

  for (const field of querySchema.fields || []) {
    if (field.computed) {
      continue
    }

    const matchingEntityField = entityFields.find((f) => f.name === field.name) ||
      entityFields.find((f) => isSingularPluralMatch(f.name, field.name))
    if (!matchingEntityField) {
      if (fieldHasNoMatchingEntityField(linkedEntity.id, field, schemas)) {
        issues.push({
          severity: Severity.MINOR,
          type: 'readmodel',
          id: card.id,
          name: readModelName,
          field: field.name,
          code: 'FIELD_NOT_IN_ENTITY',
          message: `Field "${field.name}" on read model "${readModelName}" has no matching field on entity "${linkedEntity.name}"`
        })
      }
      continue
    }

    if (field.relatedEntityId && !entitiesMap.has(field.relatedEntityId)) {
      issues.push({
        severity: Severity.MINOR,
        type: 'readmodel',
        id: card.id,
        name: readModelName,
        field: field.name,
        code: 'INVALID_ENTITY_REFERENCE',
        message: `Field "${field.name}" on read model "${readModelName}" references a non-existent entity`
      })
      continue
    }

    const entityField = matchingEntityField
    if (entityField?.relatedEntityId && field.relatedEntityId !== entityField.relatedEntityId) {
      const expectedEntity = entitiesMap.get(entityField.relatedEntityId)
      const actualEntity = field.relatedEntityId ? entitiesMap.get(field.relatedEntityId) : null

      if (expectedEntity) {
        issues.push({
          severity: Severity.MAJOR,
          type: 'readmodel',
          id: card.id,
          name: readModelName,
          field: field.name,
          code: 'ENTITY_REFERENCE_MISMATCH',
          message: actualEntity
            ? `Field "${field.name}" on read model "${readModelName}" references "${actualEntity.name}" but entity field references "${expectedEntity.name}"`
            : `Field "${field.name}" on read model "${readModelName}" should reference "${expectedEntity.name}" but no entity is selected`
        })
      }
    } else if (!entityField?.relatedEntityId && field.relatedEntityId) {
      const actualEntity = entitiesMap.get(field.relatedEntityId)
      if (actualEntity) {
        issues.push({
          severity: Severity.MAJOR,
          type: 'readmodel',
          id: card.id,
          name: readModelName,
          field: field.name,
          code: 'ENTITY_REFERENCE_MISMATCH',
          message: `Field "${field.name}" on read model "${readModelName}" references "${actualEntity.name}" but the entity field has no reference`
        })
      }
    }

    if (field.relatedEntityId && Array.isArray(field.fields) && field.fields.length > 0) {
      walkCardFields(field.relatedEntityId, field.fields, schemas, ({ field: subField, parentEntityId, path }) => {
        if (!fieldHasNoMatchingEntityField(parentEntityId, subField, schemas)) {
          return
        }
        const referencedEntity = entitiesMap.get(parentEntityId)
        const fullPath = [field.name, ...path].join('.')
        const parentPath = path.length === 1
          ? field.name
          : [field.name, ...path.slice(0, -1)].join('.')
        issues.push({
          severity: Severity.MINOR,
          type: 'readmodel',
          id: card.id,
          name: readModelName,
          field: fullPath,
          code: 'SUB_FIELD_NOT_IN_ENTITY',
          message: `Sub-field "${subField.name}" on read model "${readModelName}" (under "${parentPath}") has no matching field on entity "${referencedEntity?.name || 'unknown'}"`
        })
      })
    }
  }
}

export const validateWorkflow = (workflow, scope = 'all') => {
  const boundedContexts = workflow.boundedContexts || []
  const allIssues = []

  const globalResult = validateDomainModel(workflow, { id: null })
  allIssues.push(...(globalResult.issues || []))

  for (const bc of boundedContexts) {
    const { issues } = validateDomainModel(workflow, bc)
    allIssues.push(...(issues || []))
  }

  const dedupedIssues = deduplicateIssues(allIssues)

  const normalizedScope = String(scope || 'all').toLowerCase()
  const scopedIssues = normalizedScope === 'readmodels'
    ? dedupedIssues.filter((i) => i?.type === 'readmodel')
    : normalizedScope === 'commands'
      ? dedupedIssues.filter((i) => i?.type === 'command')
      : normalizedScope === 'entities'
        ? dedupedIssues.filter((i) => i?.type === 'entity')
        : dedupedIssues

  return {
    isValid: scopedIssues.length === 0,
    scope: normalizedScope,
    issues: scopedIssues
  }
}

export const getValidationIcon = (type) => {
  const icons = {
    entity: 'mdi-database',
    command: 'mdi-lightning-bolt',
    readmodel: 'mdi-eye-outline',
    event: 'mdi-hexagon-outline',
    role: 'mdi-account',
    gwt: 'mdi-test-tube'
  }

  return icons[type] || 'mdi-alert-circle'
}
